import { useState, useRef, useEffect } from "react";
import { Search, Plus, Tag } from "lucide-react";

function normalizeText(text) {
  return text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function searchList(query, list, displayField = "nombre") {
  const normalized = normalizeText(query);
  if (normalized.length < 2) return [];
  return list
    .filter((item) => {
      const nameMatch = normalizeText(item[displayField]).includes(normalized);
      const brandMatch = item.nombreComercial
        ? normalizeText(item.nombreComercial).includes(normalized)
        : false;
      return nameMatch || brandMatch;
    })
    .map((item) => {
      const nameMatch = normalizeText(item[displayField]).includes(normalized);
      const brandMatch = item.nombreComercial
        ? normalizeText(item.nombreComercial).includes(normalized)
        : false;
      return { ...item, _matchedByBrand: !nameMatch && brandMatch };
    })
    .slice(0, 6);
}

export function AutocompleteInput({
  label,
  list,
  displayField = "nombre",
  subtextField = "subgrupo",
  onSelect,
  onFreeText,
  placeholder = "Escribe para buscar...",
  allowFreeText = true,
  testId = "autocomplete",
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (query.length >= 2) {
      const found = searchList(query, list, displayField);
      setResults(found);
      setOpen(true);
      setHighlighted(-1);
    } else {
      setResults([]);
      setOpen(false);
    }
  }, [query, list, displayField]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (item) => {
    onSelect(item);
    setQuery("");
    setOpen(false);
  };

  const handleFreeText = () => {
    if (!query.trim() || !allowFreeText) return;
    onFreeText(query.trim());
    setQuery("");
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!open) return;
    const totalItems = results.length + (allowFreeText && query.trim() ? 1 : 0);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => (h < totalItems - 1 ? h + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => (h > 0 ? h - 1 : totalItems - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlighted >= 0 && highlighted < results.length) {
        handleSelect(results[highlighted]);
      } else if (highlighted === results.length && allowFreeText && query.trim()) {
        handleFreeText();
      } else if (allowFreeText && query.trim() && results.length === 0) {
        handleFreeText();
      }
    }
  };

  const showFreeTextOption = allowFreeText && query.trim().length >= 2 && open;

  return (
    <div ref={wrapperRef} className="relative" data-testid={testId}>
      {label && (
        <label className="block text-xs font-semibold mb-2" style={{ color: "#2D2A24" }}>
          {label}
        </label>
      )}

      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query.length >= 2 && setOpen(true)}
          placeholder={placeholder}
          className="w-full px-3 py-2.5 pr-9 rounded-xl text-sm outline-none transition-colors"
          style={{
            background: "#FCFAEE",
            border: "1px solid #E2D9CB",
            color: "#2D2A24",
            fontSize: 13,
          }}
          data-testid={`${testId}-input`}
        />
        <Search
          size={16}
          className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
          style={{ color: "#B5A998" }}
        />
      </div>

      {open && (results.length > 0 || showFreeTextOption) && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 rounded-xl overflow-hidden"
          style={{
            background: "#FFFFFF",
            border: "1px solid #E2D9CB",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            maxHeight: 280,
            overflowY: "auto",
          }}
          data-testid={`${testId}-dropdown`}
        >
          {results.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleSelect(item)}
              className="w-full text-left px-3 py-2.5 transition-colors cursor-pointer"
              style={{
                background: highlighted === i ? "#F5EFE2" : "transparent",
                borderBottom: i < results.length - 1 || showFreeTextOption ? "1px solid #F0EBE1" : "none",
              }}
              onMouseEnter={() => setHighlighted(i)}
              data-testid={`${testId}-option-${i}`}
            >
              <span className="block text-sm" style={{ color: "#2D2A24", fontWeight: 500 }}>
                {item[displayField]}
              </span>
              <span className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                {item[subtextField] && (
                  <span className="text-xs" style={{ color: "#B5A998" }}>
                    {item.categoria !== item[subtextField]
                      ? `${item.categoria} · ${item[subtextField]}`
                      : item.categoria}
                  </span>
                )}
                {item.nombreComercial && (
                  <span
                    className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded"
                    style={{
                      background: item._matchedByBrand ? "#FFF3CD" : "#F0EBE1",
                      color: item._matchedByBrand ? "#856404" : "#71644C",
                      fontWeight: item._matchedByBrand ? 600 : 400,
                    }}
                  >
                    <Tag size={10} />
                    {item.nombreComercial}
                  </span>
                )}
              </span>
            </button>
          ))}

          {showFreeTextOption && (
            <button
              type="button"
              onClick={handleFreeText}
              className="w-full text-left px-3 py-2.5 flex items-center gap-2 cursor-pointer transition-colors"
              style={{
                background: highlighted === results.length ? "#F5EFE2" : "transparent",
                fontStyle: "italic",
              }}
              onMouseEnter={() => setHighlighted(results.length)}
              data-testid={`${testId}-freetext`}
            >
              <Plus size={14} style={{ color: "#00628F" }} />
              <span className="text-sm" style={{ color: "#00628F" }}>
                Agregar "{query}"
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
